import { useRef } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Brush, FilePenLine, HeartHandshake, MoveHorizontal, QrCode, Share2 } from "lucide-react";
import digitalDiary from "@/assets/digital-planner/digital-diary.png";
import mindCanvas from "@/assets/digital-planner/mind-canvas.png";
import futureLetter from "@/assets/digital-planner/future-letter.png";
import memoryQr from "@/assets/digital-planner/memory-qr.png";

const slides = [
  {
    image: digitalDiary,
    title: "Editable Digital Diary",
    text: "Turn planner pages into a living workspace for packing, induction prep, committee tracking, and reflection.",
  },
  {
    image: mindCanvas,
    title: "Mind Canvas",
    text: "Doodle ideas, map thoughts, and clear your head when MBA life starts feeling too crowded.",
  },
  {
    image: futureLetter,
    title: "Future Letter",
    text: "Write to your future self, seal it, and open it later when you need perspective.",
  },
  {
    image: memoryQr,
    title: "Memory QR",
    text: "Create private, shareable, or password-protected QR links for selected planner memories.",
  },
];

const deliverables = [
  { icon: FilePenLine, label: "Editable planner pages", text: "Update your MBA templates without reprinting." },
  { icon: QrCode, label: "QR access", text: "Open your digital diary and planner tools from the physical planner." },
  { icon: Brush, label: "Creative space", text: "Use canvas tools for quick thoughts, notes, and mind maps." },
  { icon: Share2, label: "Shareable memories", text: "Generate controlled QR links for pages you choose." },
];

export default function DigitalPlannerShowcase() {
  const sliderRef = useRef(null);

  const scrollSlides = (direction) => {
    const slider = sliderRef.current;
    if (!slider) return;
    slider.scrollBy({
      left: direction * Math.min(slider.clientWidth * 0.9, 760),
      behavior: "smooth",
    });
  };

  return (
    <section id="digital-planner" className="overflow-hidden bg-[#073C2F] px-4 py-16 text-[#FBF7EF] sm:px-5 md:px-10 md:py-20">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
          <div>
            <p className="font-body text-sm font-bold uppercase tracking-[0.28em] text-[#D5AD76]">Digital Companion</p>
            <h2 className="mt-3 font-heading text-3xl uppercase leading-[1.04] sm:text-4xl md:text-6xl">
              Change your MBA life with a digital planner alongside your physical planner.
            </h2>
          </div>
          <div className="rounded-lg border border-[#D5AD76]/70 bg-white/8 p-5 backdrop-blur">
            <div className="flex items-center gap-3 text-[#D5AD76]">
              <HeartHandshake size={24} />
              <p className="font-heading text-lg uppercase text-[#FBF7EF] sm:text-xl">What we are delivering</p>
            </div>
            <p className="mt-3 font-body text-base leading-7 text-[#FBF7EF]/78">
              Your physical MBA planner gives structure on paper. The digital companion keeps your diary, planner pages,
              canvas, future letters, and QR memories accessible whenever you need them.
            </p>
          </div>
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-4">
          {deliverables.map((item, index) => (
            <motion.article
              key={item.label}
              className="rounded-lg border border-[#D5AD76]/60 bg-[#FBF7EF] p-5 text-[#073C2F]"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
            >
              <item.icon size={24} />
              <h3 className="mt-4 font-heading text-lg uppercase">{item.label}</h3>
              <p className="mt-2 font-body text-sm leading-6 text-[#27463B]/80">{item.text}</p>
            </motion.article>
          ))}
        </div>

        <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between md:mt-12">
          <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[#D5AD76]/70 bg-white/10 px-4 py-2 font-body text-sm font-bold text-[#FBF7EF]">
            <MoveHorizontal size={18} className="text-[#D5AD76]" />
            Slide to explore digital planner previews
            <ArrowRight size={16} className="text-[#D5AD76]" />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => scrollSlides(-1)}
              className="grid h-11 w-11 place-items-center rounded-full border border-[#D5AD76] bg-[#FBF7EF] text-[#073C2F] shadow-sm transition hover:bg-retro-yellow"
              aria-label="Previous digital planner preview"
            >
              <ArrowLeft size={20} />
            </button>
            <button
              type="button"
              onClick={() => scrollSlides(1)}
              className="grid h-11 w-11 place-items-center rounded-full border border-[#D5AD76] bg-retro-yellow text-[#073C2F] shadow-sm transition hover:bg-[#FBF7EF]"
              aria-label="Next digital planner preview"
            >
              <ArrowRight size={20} />
            </button>
          </div>
        </div>

        <div ref={sliderRef} className="mt-4 flex snap-x gap-4 overflow-x-auto pb-6 [scrollbar-width:thin] md:gap-6">
          {slides.map((slide, index) => (
            <motion.article
              key={slide.title}
              className="min-w-[88vw] snap-center overflow-hidden rounded-lg border border-[#D5AD76] bg-[#FBF7EF] text-[#073C2F] shadow-[0_24px_80px_rgba(0,0,0,0.24)] sm:min-w-[76vw] md:min-w-[720px]"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, delay: index * 0.06 }}
            >
              <img src={slide.image} alt={`${slide.title} preview`} className="aspect-[16/9] w-full object-cover object-top" loading="lazy" />
              <div className="p-5">
                <p className="font-body text-xs font-bold uppercase tracking-[0.24em] text-[#C99455]">Preview {String(index + 1).padStart(2, "0")}</p>
                <h3 className="mt-2 font-heading text-xl uppercase sm:text-2xl">{slide.title}</h3>
                <p className="mt-2 font-body text-sm leading-6 text-[#27463B]/82">{slide.text}</p>
              </div>
            </motion.article>
          ))}
        </div>

        <p className="mt-2 max-w-3xl font-body text-sm leading-7 text-[#FBF7EF]/70">
        </p>
      </div>
    </section>
  );
}
